import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { StateOption } from '../lib/api';

interface StateAutocompleteProps {
  id?: string;
  states: StateOption[];
  value: string; // selected state code
  onChange: (code: string) => void;
  onBlur?: () => void;
  error?: boolean;
  className?: string;
}

// Dependency-free combobox: type to filter states by name, click or keyboard to
// select. Commits the state code to the form; the visible text shows the name.
export default function StateAutocomplete({
  id,
  states,
  value,
  onChange,
  onBlur,
  error,
  className,
}: StateAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const focused = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;

  const selectedName = states.find((s) => s.code === value)?.name ?? '';

  // Keep the visible text in sync with the selected value when not typing
  // (e.g. states load after mount, or the form resets).
  useEffect(() => {
    if (!focused.current) setQuery(selectedName);
  }, [selectedName]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? states.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase() === q)
    : states;

  const select = (s: StateOption) => {
    onChange(s.code);
    setQuery(s.name);
    setOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[highlight]) {
        e.preventDefault();
        select(filtered[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-invalid={error}
        autoComplete="off"
        className={className}
        placeholder="Type a state…"
        value={query}
        onFocus={() => {
          focused.current = true;
          setOpen(true);
          setHighlight(0);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          focused.current = false;
          setOpen(false);
          // Revert the text to the committed selection (or clear it).
          setQuery(states.find((s) => s.code === valueRef.current)?.name ?? '');
          onBlur?.();
        }}
      />
      {open && filtered.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-fb-border bg-white py-1 shadow-lg"
        >
          {filtered.map((s, i) => (
            <li
              key={s.code}
              role="option"
              aria-selected={i === highlight}
              // mousedown (not click) + preventDefault → select before the input blurs.
              onMouseDown={(e) => {
                e.preventDefault();
                select(s);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`cursor-pointer px-4 py-2 text-[15px] ${
                i === highlight ? 'bg-fb-light text-fb-navy' : 'text-fb-ink'
              }`}
            >
              {s.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
